import React, { FC, useEffect, useMemo, useState } from 'react';
import './cancel-smart-modal.scss';
import GenericModal from '../generic-modal/generic-modal';
import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useStores } from '../../store';
import { observer } from 'mobx-react-lite';
import usePromiseFeedback from '../promise-feedback/usePromiseFeedback';
import { EventType } from '@src/dtos/analytics.dto';

interface CancelSmartModalProps {
    visible: boolean;
    onClose: () => void;
    connectionId: number;
    showCancelAll?: boolean;
}

const CancelSmartModal: FC<CancelSmartModalProps> = observer(({ visible, onClose, connectionId, showCancelAll = true }) => {
    const [cancelAll, setCancelAll] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { customerStore } = useStores();
    const [promiseFeedbackFactory] = usePromiseFeedback();

    useEffect(() => {
        if(!visible) {
            setCancelAll(false);
            setIsLoading(false);
        }
    }, [visible]);

    const handleCheckboxChange = (e: CheckboxChangeEvent) => {
        setCancelAll(e.target.checked);
    };

    const connectionsUnderAnalysis = useMemo(() =>
        customerStore.connectionList?.filter(_ => _.isInAnalysis && _.analysisDaysLeft > 0) ?? [], [customerStore.connectionList]);

    const handleConfirm = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, {
            button: 'yesCancel',
            location: 'cancelNetworkAnalysisModal',
            cancelAnalysisOnAllConnectionsBeingAnalyzed: cancelAll,
        });
        setIsLoading(true);
        promiseFeedbackFactory({
            promise: customerStore.rollbackPolicyMode(cancelAll ? connectionsUnderAnalysis.map(_=> _.id) : [connectionId]),
            successCallback: () => {
                onClose();
            },
            errorCallback: () => setIsLoading(false),
            notifObj: { notificationMsg: 'Smart protection network analysis cancelled' }
        });
    };

    const handleClose = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, {
            button: 'keepAnalysis',
            location: 'cancelNetworkAnalysisModal',
            cancelAnalysisOnAllConnectionsBeingAnalyzed: cancelAll,
        });
        onClose();
    };

    return (
        <GenericModal
            wrapClassName="cancel-smart-modal"
            open={visible}
            title="Cancel Network Analysis"
            closable={false}
            width={562}
            body={
                <div className="modal-body cancel-smart-modal-body">
                    <p>Are you sure you want to cancel network analysis?</p>
                    {showCancelAll && <Checkbox checked={cancelAll} onChange={handleCheckboxChange}>
                        Cancel analysis on all connections being analyzed ({connectionsUnderAnalysis.length})
                    </Checkbox>}
                </div>
            }
            actionButtons={[
                {
                    text: 'Keep analysis',
                    onClick: handleClose,
                    className: `secondary-btn`,
                    disabled: isLoading
                },
                {
                    text: 'Yes, Cancel',
                    onClick: handleConfirm,
                    className: `primary-btn ${isLoading ? 'loading' : ''}`,
                    disabled: isLoading
                }
            ]}
            onCancel={onClose}
        />
    );
});

export default CancelSmartModal;